import { useCallback, useEffect, useRef, useState } from "react";
import RichText from "./RichText";

/**
 * Rich-text body that collapses to a fixed height and offers "Read more".
 *
 * A text lesson is often the WHOLE lesson — creators paste a full training
 * article in — and at full height it pushed the notes / discussion / resources
 * tabs (and, on a video lesson, everything under the player) so far down the
 * page that members did not know they existed. Clamping puts the rest of the
 * lesson back within reach while leaving the body one click away.
 *
 * The clamp is MEASURED, never assumed: content shorter than the limit renders
 * untouched with no button at all, so a two-line description does not grow a
 * "Read more" that reveals nothing. Height is re-measured on resize and after
 * images/embeds inside the body load, both of which change it well after first
 * paint.
 */

/** Collapsed height. Roughly a screenful of body copy on a laptop. */
const COLLAPSED_PX = 420;
/**
 * How much taller than the clamp the content must be before it is worth
 * collapsing. Without this, a body a line or two over the limit hides almost
 * nothing behind a button that costs a click and a scroll to undo.
 */
const SLACK_PX = 140;

export default function ClampedRichText({
    html,
    className = "",
    primary = "#0274F7",
    collapsedHeight = COLLAPSED_PX,
    moreLabel = "Read more",
    lessLabel = "Show less",
}) {
    const boxRef = useRef(null);     // the clipping frame
    const innerRef = useRef(null);   // natural-height content, never clipped
    const [natural, setNatural] = useState(0);
    const [open, setOpen] = useState(false);

    // A new lesson starts collapsed again, however far the last one was opened.
    useEffect(() => { setOpen(false); }, [html]);

    useEffect(() => {
        const el = innerRef.current;
        if (!el) return undefined;
        // offsetHeight of the INNER element: the frame around it is the thing
        // being clipped, so this stays the full height at every open state.
        const measure = () => setNatural(el.offsetHeight);
        measure();

        let ro = null;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(measure);
            ro.observe(el);
        }
        // An <img> or <iframe> in the body reports no height until it loads, so
        // the first measurement can be far short of the truth.
        const media = el.querySelectorAll("img, iframe");
        media.forEach((m) => m.addEventListener("load", measure));
        window.addEventListener("resize", measure);

        return () => {
            ro?.disconnect();
            media.forEach((m) => m.removeEventListener("load", measure));
            window.removeEventListener("resize", measure);
        };
    }, [html]);

    /* Collapsing from far down the page leaves the member staring at whatever
       happened to be below the body. Bring the block back under the sticky
       header — measured, since its height differs per template. */
    const restoreScroll = useCallback(() => {
        const box = boxRef.current;
        if (!box || typeof window === "undefined") return;
        const header = document.querySelector("header");
        const stuck = header && window.getComputedStyle(header).position === "sticky"
            ? header.offsetHeight
            : 0;
        const top = box.getBoundingClientRect().top;
        if (top >= stuck) return; // already on screen — leave the page alone
        window.scrollTo({
            top: Math.max(0, top + window.scrollY - stuck - 12),
            behavior: "smooth",
        });
    }, []);

    const toggle = () => {
        setOpen((o) => {
            if (o) requestAnimationFrame(restoreScroll);
            return !o;
        });
    };

    if (!html) return null;

    // Measured at 0 on the very first render: show the body whole rather than
    // clamped, so content can never flash behind a button that then vanishes.
    // An embedded page (<iframe>) is the lesson itself, often a tool the member
    // uses in place; clipping it hides the controls, so it always shows whole.
    const embeds = /<iframe[\s>]/i.test(html);
    const clamps = !embeds && natural > 0 && natural > collapsedHeight + SLACK_PX;
    const collapsed = clamps && !open;

    return (
        <div className={className}>
            <div
                ref={boxRef}
                className="relative overflow-hidden transition-[max-height] duration-300 ease-out"
                style={{ maxHeight: collapsed ? collapsedHeight : (clamps ? natural : "none") }}
            >
                <div ref={innerRef}>
                    <RichText html={html} />
                </div>
                {/* Fades the last line into the card instead of guillotining it,
                    which is what tells the reader there IS more. pointer-events
                    off so it can never eat a click on a link underneath. */}
                {collapsed && (
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-surface via-surface/85 to-transparent"
                    />
                )}
            </div>

            {clamps && (
                <button
                    type="button"
                    onClick={toggle}
                    aria-expanded={open}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-surface px-3.5 py-1.5 text-[13px] font-bold shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-md active:translate-y-0"
                    style={{ color: primary }}
                >
                    {open ? lessLabel : moreLabel}
                    <svg
                        className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M6 9l6 6 6-6" />
                    </svg>
                </button>
            )}
        </div>
    );
}
